<template>
  <footer class="app-footer" :class="customClass">
    <ContainerLayout padding="28px 24px 16px">
      <div class="footer-main">
        <!-- 左侧区域：品牌信息、版权、ICP -->
        <div class="footer-left">
          <div class="brand-section">
            <div class="brand-info">
              <img src="@/assets/owl-logo.png" alt="猫头鹰Logo" class="footer-logo">
              <span class="brand-name">猫头鹰</span>
            </div>
            <div class="footer-meta" v-if="showMeta">
              <span class="version-tag">v{{ version }}</span>
            </div>
          </div>
          <div class="copyright-section">
            <p class="copyright-text">&copy; {{ currentYear }} {{ appConfig.companyName }} 保留所有权利</p>
            <p class="icp-text">
              <a :href="appConfig.icpLink" target="_blank" style="color: inherit; text-decoration: none;">ICP备案号：{{ appConfig.icpNumber }}</a>
            </p>
          </div>
        </div>

        <!-- 垂直分隔线 -->
        <div class="footer-divider-vertical"></div>

        <!-- 右侧区域：风险提示 -->
        <div class="footer-right">
          <div class="risk-disclaimer">
            <p>
              <b style="color: red">投资有风险,入市需谨慎</b>
              <br />郑重声明：本终端发布的信息来源于网络，与本终端立场无关。
              <br />本终端不保证该信息（包括但不限于文字、视频、音频、数据及图表）全部或者部分内容的准确性、真实性、完整性、有效性、及时性、原创性等。
              <br />相关信息并未经过本终端证实，不对您构成任何投资建议，据此操作，风险自担。
            </p>
          </div>
        </div>
      </div>
    </ContainerLayout>
  </footer>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import ContainerLayout from '@/components/common/ContainerLayout.vue'
import { APP_CONFIG } from '@/config/app'

interface Props {
  // 是否显示元数据（版本、构建时间等）
  showMeta?: boolean
  // 自定义样式类
  customClass?: string
  // 版本号
  version?: string
  // 构建时间
  buildTime?: string
}

withDefaults(defineProps<Props>(), {
  showMeta: false,
  customClass: '',
  version: '1.0.0',
  buildTime: '2024-01-01'
})

// 当前年份
const currentYear = computed(() => new Date().getFullYear())
const appConfig = APP_CONFIG

</script>

<style scoped>
.app-footer {
  background: rgba(15, 23, 42, 0.6); /* Semi-transparent dark to blend with page */
  backdrop-filter: blur(10px);
  border-top: 1px solid var(--glass-border);
  color: var(--text-secondary);
  margin-top: auto;
  /* box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.2); */
}

.footer-main {
  display: flex;
  align-items: flex-start;
  gap: 32px;
  min-height: 80px;
}

/* 左侧区域 */
.footer-left {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.brand-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

.brand-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.footer-logo {
  width: 24px;
  height: 24px;
  border-radius: 6px;
}

.brand-name {
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.02em;
}

.footer-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.copyright-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.copyright-text {
  margin: 0;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
}

.icp-text {
  margin: 0;
  color: var(--text-tertiary);
  font-size: 12px;
}

/* 垂直分隔线 */
.footer-divider-vertical {
  width: 1px;
  background: linear-gradient(180deg, transparent 0%, var(--glass-border) 20%, var(--glass-border) 80%, transparent 100%);
  align-self: stretch;
}

/* 右侧区域 */
.footer-right {
  flex: 3.2;
  display: flex;
  align-items: center;
}

.risk-disclaimer p {
  margin: 0;
  color: var(--text-tertiary);
  font-size: 12px;
  line-height: 1.6;
}

.version-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: var(--accent-gradient);
  border-radius: 12px;
  color: white;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .app-footer {
    padding: 10px 0;
  }

  .footer-main {
    flex-direction: column;
    gap: 12px;
    align-items: center;
    min-height: auto;
  }

  .footer-divider-vertical {
    display: none;
  }

  .footer-left,
  .footer-right {
    flex: 1;
    width: 100%;
    justify-content: center;
    align-items: center;
  }

  .footer-left {
    gap: 8px;
  }

  .brand-section {
    justify-content: center;
    margin-bottom: 4px;
    gap: 10px;
  }

  .footer-logo {
    width: 20px;
    height: 20px;
  }

  .brand-name {
    font-size: 14px;
  }

  .copyright-section {
    text-align: center;
    align-items: center;
    gap: 2px;
  }

  .copyright-text {
    font-size: 11px;
  }

  .icp-text {
    font-size: 10px;
  }

  .risk-disclaimer {
    text-align: justify;
    font-size: 10px;
    line-height: 1.5;
    opacity: 0.8;
    background: rgba(255, 255, 255, 0.03);
    padding: 8px;
    border-radius: 6px;
  }

  .risk-disclaimer p {
    line-height: 1.5;
  }

  .risk-disclaimer b {
    font-size: 10px;
  }
}

@media (max-width: 480px) {
  .brand-section {
    gap: 8px;
  }

  .brand-name {
    font-size: 13px;
  }

  .risk-disclaimer {
    font-size: 9px;
    padding: 6px;
  }
}
</style>